import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DropdownInput,
  DynamicDataTypeInput,
  DynamicDataTypeInputModesType,
  Form,
  Skeleton,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  ReportEmbeddedStyleVariant,
  ReportV3Adapter,
  Resources,
  validateWorkflowVariableWithRegex,
  WorkflowTemplateOutput,
} from "@validereinc/domain";
import { Workflows } from "@validereinc/domain-controllers";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import {
  AssessTemplatedReportForCommentsFormValuesType,
  useWorkflowConfigurationFormStore,
} from "../WorkflowConfigurationFormStore";
import { WorkflowConfigurationDrawerProps } from "../types";
import styles from "./WorkflowConfigurationAssessTemplatedReportForCommentsDetails.module.scss";

const { flattenWorkflowSteps } = Workflows.logic;

const cx = classNames.bind(styles);

const stylesToAssessOptions = Object.values(ReportEmbeddedStyleVariant).map(
  (style) => ({
    label: toTitleCaseString(style),
    value: style,
  })
);

export const WorkflowConfigurationAssessTemplatedReportForComments = ({
  stepId,
  workflowTemplate,
}: {
  stepId: string;
  workflowTemplate: WorkflowConfigurationDrawerProps["workflowTemplate"];
}) => {
  const {
    getAvailableStepVariables,
    isSelectingNodeToSourceFormDataFrom,
    setIsSelectingNodeToSourceFormDataFrom,
    nodeIdToSourceFormDataFrom,
  } = useWorkflowGraphState();

  const {
    setAssessTemplatedReportForComments,
    setAssessTemplatedReportForCommentsIsValid,
    getAssessTemplatedReportForCommentsFormValues,
  } = useWorkflowConfigurationFormStore();

  const storeValues = getAssessTemplatedReportForCommentsFormValues();

  const [reportIdInputMode, setReportIdInputMode] =
    useState<DynamicDataTypeInputModesType>("variable");

  const form = useForm<AssessTemplatedReportForCommentsFormValuesType>({
    values: storeValues,
  });

  const formValues = form.watch();

  const reportIdWorkflowVariableFromNodeToSourceFormDataFrom = useMemo(() => {
    const nodeToSourceFormDataFromDefinition = flattenWorkflowSteps(
      workflowTemplate?.config?.steps
    )?.[nodeIdToSourceFormDataFrom ?? ""];

    if (!nodeToSourceFormDataFromDefinition) {
      return;
    }

    const nodeToSourceFormDataFromTaskOutput =
      (nodeToSourceFormDataFromDefinition?.task.output ??
        {}) as WorkflowTemplateOutput;

    return Object.keys(nodeToSourceFormDataFromTaskOutput).find(
      (key) => nodeToSourceFormDataFromTaskOutput[key] === "$.report_id"
    );
  }, [nodeIdToSourceFormDataFrom]);

  const { data: reportsData, isLoading: isLoadingReports } = useQuery(
    [Resources.REPORT],
    () => {
      return ReportV3Adapter.getList({
        filters: {
          templated_report_name: { $exists: true },
          isAlreadyFormatted: true,
        },
      });
    },
    { staleTime: 10 * 60 * 1000 }
  );
  const reports = reportsData?.data ?? [];
  const latestReportVersion = useMemo(() => {
    return (
      reports.find((report) => report.id === formValues.report_id)
        ?.latest_version?.version ?? 1
    );
  }, [formValues.report_id, reports]);

  const isReportIdWorkflowVariable = validateWorkflowVariableWithRegex(
    formValues.report_id
  );

  const {
    data: reportSheetDetailsData,
    isLoading: isLoadingReportSheetDetails,
  } = useQuery(
    [
      Resources.REPORT,
      formValues.report_id,
      latestReportVersion,
      "embedded",
      "sheets",
    ],
    () => {
      if (!formValues.report_id) return;

      return ReportV3Adapter.embedded.sheets.getList({
        meta: {
          report_id: formValues.report_id,
          report_version: latestReportVersion,
        },
      });
    },
    {
      enabled:
        Boolean(formValues.report_id) &&
        !isReportIdWorkflowVariable &&
        Boolean(latestReportVersion),
      staleTime: 10 * 60 * 1000,
    }
  );
  const reportSheetDetails = reportSheetDetailsData?.data[0];
  const headerOptions = useMemo(() => {
    const headers = Object.keys(reportSheetDetails?.headers ?? {});

    return headers.map((column) => ({
      label: toTitleCaseString(column),
      value: column,
    }));
  }, [reportSheetDetails]);

  const workflowVariables = useMemo(
    () => getAvailableStepVariables(stepId ?? ""),
    [stepId]
  );
  const workflowVariableOptions = useMemo(
    () =>
      workflowVariables.map((workflowVariable) => ({
        label: workflowVariable,
        value: workflowVariable,
      })),
    [workflowVariables]
  );

  const handleReportIdChange = () => {
    form.setValue("header_name", "");
  };

  useEffect(() => {
    if (formValues.report_id) {
      setAssessTemplatedReportForComments(formValues);
    }
  }, [JSON.stringify(formValues), setAssessTemplatedReportForComments]);

  useEffect(() => {
    setAssessTemplatedReportForCommentsIsValid(form.formState.isValid);
  }, [form.formState.isValid, setAssessTemplatedReportForCommentsIsValid]);

  useEffect(() => {
    if (reportIdWorkflowVariableFromNodeToSourceFormDataFrom) {
      form.setValue(
        "report_id",
        reportIdWorkflowVariableFromNodeToSourceFormDataFrom
      );
    }
  }, [reportIdWorkflowVariableFromNodeToSourceFormDataFrom]);

  const isLoading =
    isLoadingReports ||
    (Boolean(formValues.report_id) &&
      !isReportIdWorkflowVariable &&
      Boolean(latestReportVersion) &&
      isLoadingReportSheetDetails);

  const predictedReportIdInputMode =
    isReportIdWorkflowVariable ||
    reportIdWorkflowVariableFromNodeToSourceFormDataFrom
      ? "variable"
      : "custom_answer";

  useEffect(() => {
    if (predictedReportIdInputMode !== reportIdInputMode) {
      setReportIdInputMode(predictedReportIdInputMode);
    }
  }, [predictedReportIdInputMode, reportIdInputMode, setReportIdInputMode]);

  return (
    <Skeleton
      isLoading={isLoading}
      style={{ minHeight: 200 }}
    >
      <Form {...form}>
        <div className={cx("report-id-field-container")}>
          <DynamicDataTypeInput
            name="report_id"
            label="Reference Report"
            className={cx("report-id-field")}
            mode={reportIdInputMode}
            onModeChange={setReportIdInputMode}
            dataType="pick-list"
            dropdownOptions={reports}
            //@ts-expect-error TODO: Typing on dropdownOptions
            isSortedAlphabetically
            labelKey="name"
            valueKey="id"
            noSearchDebounce
            onChange={handleReportIdChange}
            variables={workflowVariableOptions}
            isFluid
            isRequired
            slotRight={
              <Button
                onClick={() =>
                  setIsSelectingNodeToSourceFormDataFrom(
                    !isSelectingNodeToSourceFormDataFrom
                  )
                }
                className={cx("select-node-button", {
                  "is-active": isSelectingNodeToSourceFormDataFrom,
                })}
                icon="cursor-click"
              />
            }
          />
        </div>
        {!isReportIdWorkflowVariable ? (
          <DropdownInput
            name="header_name"
            label="Column to Check"
            options={headerOptions}
            placeholder="Select Column to Check..."
            valueKey="value"
            labelKey="label"
            isRequired
            isFluid
          />
        ) : (
          <TextInput
            name="header_name"
            label="Column to Check"
            placeholder="Enter Column to Check..."
            isRequired
            isFluid
          />
        )}
        <DropdownInput
          name="styles_to_assess"
          label="Cell Styles to Check"
          options={stylesToAssessOptions}
          placeholder="Select Cell Styles to Check..."
          isMulti
          valueKey="value"
          labelKey="label"
          isRequired
          rules={{
            validate: {
              mustContainAtLeastOneStyle: (value: string[]) => {
                if (!value.length) {
                  return "Please select at least one style to assess";
                }
                return true;
              },
            },
          }}
          isFluid
        />
      </Form>
    </Skeleton>
  );
};
